import Image from 'next/image';
import { useEffect, useState } from 'react';
import Dialog from '@/cms/layouts/dialogs/index';
import FolderOpenImage from '@/dashboard/assets/images/folder-open.png';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { EmojiHappyIcon } from '@heroicons/react/outline';
import {
  JoveButton,
  JoveCloudFileListItemView,
  JoveDatePickerControlled,
  JoveFileDragUploadFilesView,
  JoveLocalFileListItemView,
  JoveLogoLoading,
  JoveSelectControlled,
} from '@jove-frontend/mui';
import {
  LoadingMask,
  REQUEST_SUCCESS_CODE,
  apiDeleteUserRequestCancelPolicyUploadedDocument,
  apiGetUserRequestCancelUploadDocumentList,
  apiJudgeCurrentPolicyCanDirectlyCancel,
  apiQueryPolicyPreCancelInfo,
  apiRequestCancelPolicyWithFileInfo,
  apiSavePrePolicyCancelInfo,
  convertTimeToServerStoredTimeDataWithZeroTime,
  getDeductionMethodMap,
  useCancelPolicyRelatedInfo,
} from '@jove-frontend/uikit';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

export enum RequestCancelPolicyHandleTypeEnum {
  normal = 'normal',
  uploadFiles = 'upload_files',
}

interface RequestCancelPolicyDialogProps {
  policy: PolicyModel;
  info?: {
    companyName?: string;
  };
  close?: () => void;
  success?: () => void;
  handleTypeChange?: (handleType: RequestCancelPolicyHandleTypeEnum) => void;
}

const RequestCancelPolicyDialog = (props: RequestCancelPolicyDialogProps) => {
  const { close, success, policy, info, handleTypeChange } = props;
  const [loading, setLoading] = useState(false);
  const methods = useForm({
    defaultValues: {
      cancelDate: policy?.payload?.pre_cancel_info?.pre_cancellation_date
        ? dayjs(policy.payload.pre_cancel_info.pre_cancellation_date)
        : dayjs(new Date()).add(1, 'day'),
      cancelReason: policy?.payload?.reason || '',
    },
  });
  const { control, getValues, watch } = methods;
  const { refundPriceInfoList, evaluateCancelRefundPriceInfoList, requestPolicyCancelReasonList, cancelReasonList } = useCancelPolicyRelatedInfo();
  const [priceLoading, setPriceLoading] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [fileList, setFileList] = useState([]);
  const [cloudPolicyDocumentList, setCloudPolicyDocumentList] = useState([]);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [requestCancelPolicyData, setRequestCancelPolicyData] = useState(null);
  const [handleType, setHandleType] = useState<RequestCancelPolicyHandleTypeEnum>(RequestCancelPolicyHandleTypeEnum.normal);
  const [skipUploadDocument, setSkipUploadDocument] = useState(false);

  /**
   * requests
   */
  const requestCancelPolicy = async () => {
    const { cancelDate, cancelReason } = getValues();
    const params: any = {
      policy_id: policy?.id,
      cancellation_date: convertTimeToServerStoredTimeDataWithZeroTime(cancelDate),
    };
    if (cancelReason) {
      params.reason = cancelReason;
    }

    if (handleType === RequestCancelPolicyHandleTypeEnum.uploadFiles) {
      params.fileList = fileList.map((item) => item.file);
      params.is_apply = true;
    }

    setLoading(true);
    const [res] = await apiRequestCancelPolicyWithFileInfo(params);
    setLoading(false);

    if (res) {
      toast.success('Cancel policy successfully!');
      close?.();
      success?.();
    }
  };

  const requestCancelRefundAmount = async () => {
    setPriceLoading(true);
    const [res] = await apiQueryPolicyPreCancelInfo({
      policy_id: policy?.id,
      cancel_date: convertTimeToServerStoredTimeDataWithZeroTime(getValues('cancelDate')),
    });
    setPriceLoading(false);

    if (res?.code === REQUEST_SUCCESS_CODE) {
      evaluateCancelRefundPriceInfoList(res.data);
    }
  };

  const requestSaveCancelInfo = async () => {
    const { cancelDate, cancelReason } = getValues();

    const params: any = {
      policy_id: policy?.id,
      cancel_date: convertTimeToServerStoredTimeDataWithZeroTime(cancelDate),
    };
    if (cancelReason) {
      params.reason = cancelReason;
    }

    setLoading(true);
    const [res] = await apiSavePrePolicyCancelInfo(params);
    setLoading(false);

    if (res?.code === REQUEST_SUCCESS_CODE) {
      toast.success('Save successfully!');
      close?.();
      success?.();
    }
  };

  const requestUserRequestCancelUploadFiles = async () => {
    const [res] = await apiGetUserRequestCancelUploadDocumentList({ policyId: policy.id, hideErrToast: true });

    setCloudPolicyDocumentList(
      (res || []).map((item) => {
        return {
          ...item,
          canDelete: true,
        };
      })
    );
  };

  const requestJudgeCanCancelPolicy = async () => {
    const params = {
      policyId: policy?.id,
    };

    const [res] = await apiJudgeCurrentPolicyCanDirectlyCancel(params);

    if (res?.code === REQUEST_SUCCESS_CODE) {
      setHandleType(res.data?.canCancel === false ? RequestCancelPolicyHandleTypeEnum.uploadFiles : RequestCancelPolicyHandleTypeEnum.normal);
      setRequestCancelPolicyData(res.data);
    }
  };

  const requestDeleteCancelPolicyDocument = async (data: { id: string }) => {
    const params = { policyId: policy?.id, document_id: data.id };
    setDeleteDocumentLoading(true);
    const [res] = await apiDeleteUserRequestCancelPolicyUploadedDocument(params);

    if (res?.code === REQUEST_SUCCESS_CODE) {
      await requestUserRequestCancelUploadFiles();
    }
    setDeleteDocumentLoading(false);
  };

  /**
   * watch
   */
  const cancelDateWatch = watch('cancelDate');
  const cancelReasonWatch = watch('cancelReason');

  /**
   * effects
   */
  useEffect(() => {
    requestPolicyCancelReasonList();
    makeRequests();
  }, []);

  useEffect(() => {
    requestCancelRefundAmount();
  }, [cancelDateWatch]);

  useEffect(() => {
    setDialogTitle(handleType === RequestCancelPolicyHandleTypeEnum.uploadFiles ? 'Request to cancel the policy' : 'Cancel policy');
    handleTypeChange?.(handleType);
  }, [handleType]);

  /**
   * methods
   */
  const makeRequests = async () => {
    setSkipUploadDocument(!!policy?.payload?.need_cancellation_file);
    setLoading(true);
    await requestJudgeCanCancelPolicy();
    await requestUserRequestCancelUploadFiles();
    setLoading(false);
  };

  const selectFilesChangeAction = (files) => {
    setFileList([...fileList, ...files]);
  };

  const emitFileUploadErrorAction = (item) => {
    toast.error(item.msg);
  };

  const deleteLocalFileItemAction = (item) => {
    setFileList(fileList.filter((aItem) => aItem.id !== item.id));
  };

  const deleteCloudFileItemAction = (item) => {
    requestDeleteCancelPolicyDocument({ id: item.id });
  };

  return (
    <Dialog.Wrapper width="600px" className="relative" loading={loading}>
      <Dialog.Header close={close} title={dialogTitle}></Dialog.Header>
      <Dialog.Content>
        <FormProvider {...methods}>
          <Box className="flex flex-col space-y-6">
            {handleType === RequestCancelPolicyHandleTypeEnum.normal && (
              <Box className="flex flex-col space-y-3">
                <Typography variant="subtitle1" className="font-medium">
                  Do you want to cancel this policy?
                </Typography>
                <Typography variant="subtitle1" className="font-medium">
                  <span className="font-bold">Important:</span> If you agree to cancel the policy, it will no longer be in effect after the date
                  stated below.
                </Typography>
              </Box>
            )}
            {handleType === RequestCancelPolicyHandleTypeEnum.uploadFiles && (
              <Box className="flex flex-col space-y-2">
                <Box className="bg-brand-secondary/20 text-brand-secondary self-center rounded-full p-2">
                  <EmojiHappyIcon className="h-7 w-7" />
                </Box>
                <Typography className="text-black-secondary mt-4 text-center font-black" variant="h5">
                  Cancel Business insurance
                </Typography>
                <Typography className="text-black-secondary mt-4 text-center font-medium" variant="subtitle1">
                  Given that you are in contract with <span className="font-bold">{requestCancelPolicyData?.companyName}</span> and Jove is the
                  insurance validation partner of <span className="font-bold">{requestCancelPolicyData?.companyName}</span>, you must obtain approval
                  from <span className="font-bold">{requestCancelPolicyData?.companyName}</span> and confirm that your existing contract has ended
                  before we can cancel your insurance.
                </Typography>
              </Box>
            )}
            {!skipUploadDocument && handleType === RequestCancelPolicyHandleTypeEnum.uploadFiles && (
              <Box className={clsx('flex flex-col space-y-3')}>
                {fileList.length + cloudPolicyDocumentList.length < 5 && (
                  <JoveFileDragUploadFilesView
                    className="py-10"
                    maxFiles={5 - fileList?.length - cloudPolicyDocumentList?.length}
                    selectFiles={selectFilesChangeAction}
                    emitError={emitFileUploadErrorAction}
                    imgSlot={<Image src={FolderOpenImage} alt="Folder" height={60} width={60} />}
                  />
                )}
                <Box className="flex flex-col space-y-2">
                  {fileList.map((item) => {
                    return <JoveLocalFileListItemView info={item} className="px-3 py-2" deleteItem={deleteLocalFileItemAction} />;
                  })}
                  <Box className={clsx('relative flex flex-col space-y-2', deleteDocumentLoading ? 'min-h-[120px]' : '')}>
                    {deleteDocumentLoading && <JoveLogoLoading />}
                    {cloudPolicyDocumentList.map((item) => {
                      return <JoveCloudFileListItemView info={item} className="px-3 py-2" deleteItem={deleteCloudFileItemAction} />;
                    })}
                  </Box>
                </Box>
              </Box>
            )}
            <Box className="flex flex-col space-y-3">
              <JoveDatePickerControlled
                name="cancelDate"
                control={control}
                minDate={dayjs(new Date()).add(1, 'day')}
                label="Cancellation date"
                containerClassName="w-full"
                hideClearButton
              />
              <JoveSelectControlled
                name="cancelReason"
                control={control}
                options={cancelReasonList}
                label="Cancellation reason"
                containerClassName="w-full"
                hideCloseBtn
              />
              <Box className="flex flex-row justify-between">
                <Typography variant="subtitle1" className="font-medium">
                  Policy type
                </Typography>
                <Typography variant="subtitle1" className="font-bold">
                  {getDeductionMethodMap()[policy?.quotePayload?.billingCycle]?.label}
                </Typography>
              </Box>
              <Box className={clsx('relative flex flex-col space-y-1', priceLoading ? 'min-h-[100px]' : '')}>
                {priceLoading && <LoadingMask />}
                {refundPriceInfoList.map((item, index) => {
                  return (
                    <Box key={index} className="flex flex-row items-center justify-between">
                      <Typography variant="subtitle1" className={clsx(item.bolder ? 'font-black' : 'font-medium')}>
                        {item.title}
                      </Typography>
                      <Typography variant="subtitle1" className={clsx(item.bolder ? 'font-black' : 'font-bold')}>
                        {item.content}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </FormProvider>
      </Dialog.Content>
      <Dialog.Footer hideTopLine>
        {handleType === RequestCancelPolicyHandleTypeEnum.normal && (
          <Box className="flex flex-row justify-end space-x-2">
            {/* <JoveButton variant="text" disabled={!cancelReasonWatch} onClick={requestSaveCancelInfo}>
              Save information without cancelling
            </JoveButton> */}
            <JoveButton variant="contained" loading={priceLoading} disabled={!cancelReasonWatch} onClick={requestCancelPolicy}>
              Submit
            </JoveButton>
          </Box>
        )}
        {handleType === RequestCancelPolicyHandleTypeEnum.uploadFiles && (
          <Box className="flex flex-row justify-end space-x-2">
            <JoveButton variant="text" onClick={close}>
              Cancel
            </JoveButton>
            <JoveButton
              variant="contained"
              disabled={(!skipUploadDocument && fileList.length === 0 && cloudPolicyDocumentList.length === 0) || !cancelReasonWatch}
              loading={priceLoading}
              onClick={requestCancelPolicy}
            >
              Submit
            </JoveButton>
          </Box>
        )}
      </Dialog.Footer>
    </Dialog.Wrapper>
  );
};

export default RequestCancelPolicyDialog;
